// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { classifyPlate, type PlateFeature } from './geo';
import { coarseRegion } from './model';
import type { FeedResult, MagBand, Period, Quake } from './types';

export type Status = 'loading' | 'ok' | 'error';

export interface AppState {
  period: Period;
  band: MagBand;
  view: string;
  feed: FeedResult | null;
  status: Status;
  error: string;
  lastFetched: number;
  plates: PlateFeature[];
  /** Cache: quake id → tectonic plate name (point-in-polygon is not free). */
  plateCache: Map<string, string>;
}

const PREF_KEY = 'quakewatch.prefs.v1';
const VALID_PERIODS: Period[] = ['hour', 'day', 'week', 'month'];
const VALID_BANDS: MagBand[] = ['all', '2.5', '4.5', 'significant'];

function loadPrefs(): { period: Period; band: MagBand } {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        period: VALID_PERIODS.includes(p.period) ? p.period : 'day',
        band: VALID_BANDS.includes(p.band) ? p.band : 'all',
      };
    }
  } catch {
    /* ignore */
  }
  return { period: 'day', band: 'all' };
}

const prefs = loadPrefs();

export const state: AppState = {
  period: prefs.period,
  band: prefs.band,
  view: 'overview',
  feed: null,
  status: 'loading',
  error: '',
  lastFetched: 0,
  plates: [],
  plateCache: new Map(),
};

export function savePrefs(): void {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify({ period: state.period, band: state.band }));
  } catch {
    /* ignore */
  }
}

/** All events in the current feed (may include quarry blasts / explosions). */
export function quakes(): Quake[] {
  return state.feed?.quakes ?? [];
}

/** Only natural earthquakes (excludes explosions / quarry blasts). */
export function naturalQuakes(): Quake[] {
  return quakes().filter((q) => q.type === 'earthquake');
}

/** Plate name for a quake, cached. Empty string if unclassifiable / no plates loaded. */
export function plateOf(q: Quake): string {
  const hit = state.plateCache.get(q.id);
  if (hit !== undefined) return hit;
  const name = state.plates.length ? classifyPlate(q.lat, q.lon, state.plates) : '';
  state.plateCache.set(q.id, name);
  return name;
}

/** Region label for grouping — plate name if known, else the coarse geographic region. */
export function regionOf(q: Quake): string {
  const plate = plateOf(q);
  if (plate) return plate;
  return coarseRegion(q.lat, q.lon);
}

export function largest(list: Quake[] = quakes()): Quake | null {
  let best: Quake | null = null;
  for (const q of list) if (!best || q.mag > best.mag) best = q;
  return best;
}

export function byId(id: string): Quake | undefined {
  return quakes().find((q) => q.id === id);
}
